import { z } from 'zod';
import { addDaysToLocalDate, localDateOf } from './timezone.js';
/**
 * Faltas, reposição e saldo (P2 do personal; estudo: "Falta e reposição na agenda, saldo do
 * pacote, aluno sem treinar há N dias"). A regra é a que os estúdios já usam (Tecnofit, Nextfit):
 * desmarcou com a antecedência combinada, não perde a sessão; faltou ou desmarcou em cima, a
 * sessão conta. O personal abona o caso justificado. Na mensalidade, desmarcar a tempo vale uma
 * reposição com prazo; no pacote, a sessão simplesmente continua no saldo.
 */
export const absencePolicySchema = z.object({
    /** Horas de antecedência para desmarcar sem perder a sessão (0: desmarcar nunca conta). */
    noticeHours: z.number().int().min(0).max(72),
    /** Dias para repor a sessão desmarcada a tempo (mensalidade). */
    makeupDays: z.number().int().min(1).max(120),
    /** Falta sem aviso conta como sessão feita. */
    noShowCounts: z.boolean(),
});
export const DEFAULT_ABSENCE_POLICY = {
    noticeHours: 12,
    makeupDays: 30,
    noShowCounts: true,
};
export const ABSENCE_NOTICE_OPTIONS = [0, 2, 6, 12, 24, 48];
export const ABSENCE_MAKEUP_OPTIONS = [7, 15, 30, 60];
export const SESSION_FATES = ['feita', 'descontada', 'abonada', 'desmarcada', 'marcada'];
export const SESSION_FATE_LABEL = {
    feita: 'Feita',
    descontada: 'Descontada',
    abonada: 'Abonada',
    desmarcada: 'Desmarcada a tempo',
    marcada: 'Marcada',
};
/** Avaliação física não sai do pacote (é serviço à parte). */
export const countsAsSession = (label) => !/avalia/i.test(label);
/** Desmarcada (pela clínica ou pelo aluno) com menos antecedência que a regra pede. */
export function cancelledLate(slot, policy) {
    if (slot.status !== 'cancelado' || !slot.cancelledAt || policy.noticeHours === 0)
        return false;
    return Date.parse(slot.startAt) - Date.parse(slot.cancelledAt) < policy.noticeHours * 3_600_000;
}
/** O destino de um horário na conta: feita, descontada, abonada, desmarcada a tempo, marcada. */
export function sessionFate(slot, policy, now) {
    if (slot.status === 'compareceu')
        return 'feita';
    if (slot.status === 'faltou')
        return slot.sessionCharge === 'abonada' || !policy.noShowCounts ? 'abonada' : 'descontada';
    if (slot.status === 'cancelado') {
        if (!cancelledLate(slot, policy))
            return 'desmarcada';
        return slot.sessionCharge === 'abonada' || !policy.noShowCounts ? 'abonada' : 'descontada';
    }
    // Marcada no futuro; a que já passou sem presença marcada fica fora da conta até alguém marcar.
    return Date.parse(slot.startAt) >= now.getTime() ? 'marcada' : null;
}
export const sessionBalanceSchema = z.object({
    kind: z.enum(['pacote', 'mensal']),
    /** Pacote: quantas foram compradas. */
    total: z.number().int().nullable(),
    /** Feitas mais as descontadas (faltas e desmarcadas em cima). */
    used: z.number().int(),
    done: z.number().int(),
    charged: z.number().int(),
    excused: z.number().int(),
    booked: z.number().int(),
    /** Pacote: o que resta. */
    left: z.number().int().nullable(),
    /** Mensalidade: as reposições abertas, com o prazo. */
    makeups: z.array(z.object({ fromId: z.string(), label: z.string(), day: z.string(), until: z.string() })),
    policy: absencePolicySchema,
});
/**
 * O saldo do plano do aluno: conta os horários desde o começo do plano (avaliação fora). Só
 * pacote e mensalidade têm saldo; outro tipo de plano devolve nulo.
 */
export function sessionBalance(input) {
    const { plan, policy, now, timeZone } = input;
    const kind = plan.sessions ? 'pacote' : plan.renews ? 'mensal' : null;
    if (!kind)
        return null;
    const today = localDateOf(now, timeZone);
    const mine = input.slots.filter((slot) => countsAsSession(slot.label) && localDateOf(new Date(slot.startAt), timeZone) >= plan.startDay);
    const count = { feita: 0, descontada: 0, abonada: 0, desmarcada: 0, marcada: 0 };
    for (const slot of mine) {
        const fate = sessionFate(slot, policy, now);
        if (fate)
            count[fate] += 1;
    }
    const used = count.feita + count.descontada;
    // Reposição: a desmarcada a tempo vale uma sessão a mais até o prazo; repor é marcar com ela.
    const spent = new Set(input.slots
        .filter((slot) => slot.makeupOf && slot.status !== 'cancelado')
        .map((slot) => slot.makeupOf));
    const makeups = kind === 'mensal'
        ? mine
            .filter((slot) => sessionFate(slot, policy, now) === 'desmarcada' && !spent.has(slot.id))
            .map((slot) => {
            const day = localDateOf(new Date(slot.startAt), timeZone);
            return {
                fromId: slot.id,
                label: slot.label,
                day,
                until: addDaysToLocalDate(day, policy.makeupDays),
            };
        })
            .filter((row) => row.until >= today)
            .sort((a, b) => a.until.localeCompare(b.until))
        : [];
    return {
        kind,
        total: plan.sessions ?? null,
        used,
        done: count.feita,
        charged: count.descontada,
        excused: count.abonada,
        booked: count.marcada,
        left: plan.sessions ? Math.max(0, plan.sessions - used) : null,
        makeups,
        policy,
    };
}
/** Desmarcar agora: quantas horas faltam e se, pela regra, a sessão ainda é preservada. */
export function cancelOutlook(startAt, policy, now) {
    const hoursLeft = Math.max(0, Math.floor((Date.parse(startAt) - now.getTime()) / 3_600_000));
    return {
        hoursLeft,
        inTime: policy.noticeHours === 0 ||
            Date.parse(startAt) - now.getTime() >= policy.noticeHours * 3_600_000,
    };
}
